import { ArrowLeft, Users, Calendar, CheckCircle, Settings, Code, Cloud, Database, Layers, Shield, Zap, Globe } from 'lucide-react'
import { Link } from 'react-router-dom'

const features = [
  {
    icon: <Users size={24} />,
    title: 'User Authentication',
    description: 'Secure user registration and login system with role-based access control. Household members can be invited via email links with admin approval.',
  },
  {
    icon: <Calendar size={24} />,
    title: 'Task Scheduling with Rotation',
    description: 'Automated rotation algorithm that fairly distributes kitchen cleaning tasks among household members with configurable schedules and frequency.',
  },
  {
    icon: <CheckCircle size={24} />,
    title: 'Completion Tracking',
    description: 'Real-time task completion status with timestamps, streak tracking, and overdue notifications. Visual history of all completed and pending tasks.',
  },
  {
    icon: <Settings size={24} />,
    title: 'Admin Panel',
    description: 'Full administrative dashboard for managing household members, configuring task templates, adjusting rotation rules, and viewing analytics.',
  },
]

const architectureLayers = [
  {
    name: 'Frontend',
    tech: 'Next.js 15 + React 19',
    components: 'App Router, Server Components, Client Components, Tailwind CSS',
    description: 'Modern full-stack React with App Router architecture. Server Components for initial data fetching and SEO, Client Components for interactive task management UI.',
  },
  {
    name: 'Backend',
    tech: 'Next.js API Routes + Server Actions',
    components: 'Authentication, Task CRUD, Rotation Logic, Admin APIs',
    description: 'Server-side logic using Next.js Server Actions for mutations and API routes for external integrations. Type-safe with shared TypeScript interfaces.',
  },
  {
    name: 'Database',
    tech: 'Cloudflare D1 (SQLite)',
    components: 'Users, Tasks, Rotations, Households tables',
    description: 'Serverless SQLite database via Cloudflare D1 with Prisma ORM for type-safe queries, migrations, and schema management.',
  },
  {
    name: 'Deployment',
    tech: 'Cloudflare Pages',
    components: 'CI/CD via Wrangler, Edge Network, Custom Domains',
    description: 'Deployed to Cloudflare Pages with automatic CI/CD on git push. Edge network distribution for low-latency global access with Wrangler CLI tooling.',
  },
]

export default function KitchenRotaPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-xl border-b border-white/5">
        <div className="flex justify-between items-center px-8 py-4 max-w-7xl mx-auto">
          <Link to="/" className="text-xl font-bold tracking-tighter text-white font-headline">
            Neural Slate
          </Link>
          <Link
            to="/#projects"
            className="text-on-surface-variant hover:text-primary transition-colors flex items-center gap-2"
          >
            <ArrowLeft size={16} />
            Back to Projects
          </Link>
        </div>
      </header>

      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative min-h-[600px] flex items-center px-8 overflow-hidden">
          <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-primary/10 to-transparent blur-3xl pointer-events-none"></div>
          <div className="max-w-7xl mx-auto w-full relative z-10">
            <div className="max-w-4xl">
              <span className="inline-block py-1 px-3 mb-6 bg-surface-container-high border border-outline-variant/20 rounded-full text-primary text-xs font-mono tracking-widest uppercase">
                Full-Stack Web App · Next.js 15
              </span>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tighter mb-8 leading-tight font-headline text-white">
                KitchenRota <br />
                <span className="text-primary">Shared Kitchen Manager</span>
              </h1>
              <p className="text-xl md:text-2xl text-on-surface-variant font-light mb-12 max-w-2xl leading-relaxed">
                Full-stack web application for managing shared kitchen cleaning responsibilities with automated task rotation and completion tracking.
              </p>
              <div className="flex flex-wrap gap-8 items-center">
                <div className="flex flex-col">
                  <span className="text-primary text-4xl font-bold tracking-tighter font-headline">Next.js</span>
                  <span className="text-on-surface-variant text-sm uppercase tracking-widest font-medium">15 + React 19</span>
                </div>
                <div className="h-12 w-px bg-outline-variant/30"></div>
                <div className="flex flex-col">
                  <span className="text-primary text-4xl font-bold tracking-tighter font-headline">D1</span>
                  <span className="text-on-surface-variant text-sm uppercase tracking-widest font-medium">Cloudflare DB</span>
                </div>
                <div className="h-12 w-px bg-outline-variant/30"></div>
                <div className="flex flex-col">
                  <span className="text-secondary text-4xl font-bold tracking-tighter font-headline">Edge</span>
                  <span className="text-on-surface-variant text-sm uppercase tracking-widest font-medium">Global Deploy</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Overview */}
        <section className="py-24 px-8 bg-surface-container-lowest">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
              <div className="lg:col-span-1">
                <h2 className="text-4xl font-bold mb-6 text-white font-headline">Project Overview</h2>
                <p className="text-on-surface-variant mb-8 leading-relaxed">
                  KitchenRota solves the age-old flatmate dilemma of who cleans the kitchen. It automatically assigns cleaning tasks on a rotating schedule, tracks completion, and provides full transparency through an admin dashboard.
                </p>
                <div className="p-6 bg-surface-container-low rounded-xl border border-outline-variant/10">
                  <Zap className="text-primary mb-3" size={28} />
                  <h4 className="font-bold mb-2 text-white">Fair Rotation</h4>
                  <p className="text-sm text-on-surface-variant leading-relaxed">
                    Intelligent rotation algorithm ensures equitable distribution of tasks, accounting for absences, task difficulty, and historical completion rates.
                  </p>
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {features.map((feature, index) => (
                    <div key={index} className="bg-surface-container-low p-6 rounded-xl border border-outline-variant/10 hover:border-primary/30 transition-all">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                        {feature.icon}
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2 font-headline">{feature.title}</h3>
                      <p className="text-sm text-on-surface-variant leading-relaxed">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Architecture */}
        <section className="py-24 px-8 bg-surface">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4 text-white font-headline">Architecture</h2>
              <p className="text-on-surface-variant max-w-2xl mx-auto">
                Full-stack architecture leveraging Next.js 15 App Router for unified frontend and backend, with Cloudflare D1 for edge-compatible database storage.
              </p>
            </div>
            <div className="space-y-6">
              {architectureLayers.map((layer, index) => (
                <div key={index} className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10 flex flex-col md:flex-row gap-6 items-start">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                    {index === 0 && <Layers size={24} />}
                    {index === 1 && <Code size={24} />}
                    {index === 2 && <Database size={24} />}
                    {index === 3 && <Cloud size={24} />}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-white font-headline">{layer.name}</h3>
                      <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-1 rounded">{layer.tech}</span>
                    </div>
                    <p className="text-sm font-mono text-on-surface-variant/70 mb-3">{layer.components}</p>
                    <p className="text-on-surface-variant leading-relaxed">{layer.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tech Stack */}
        <section className="py-24 px-8 bg-surface-container-lowest">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
              <div className="lg:col-span-1">
                <h2 className="text-4xl font-bold mb-6 text-white font-headline">Tech Stack</h2>
                <p className="text-on-surface-variant mb-8 leading-relaxed">
                  Modern serverless stack optimized for developer experience, type safety, and edge deployment with zero infrastructure management.
                </p>
                <div className="p-6 bg-surface-container-low rounded-xl border border-outline-variant/10">
                  <Globe className="text-primary mb-3" size={28} />
                  <h4 className="font-bold mb-2 text-white">Cloudflare Ecosystem</h4>
                  <p className="text-sm text-on-surface-variant leading-relaxed">
                    Pages for hosting, D1 for database, and Workers for serverless compute — all deployed through a single Wrangler configuration with instant global propagation.
                  </p>
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="overflow-hidden rounded-xl border border-outline-variant/15">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-surface-container-high text-on-surface uppercase text-xs tracking-widest">
                      <tr>
                        <th className="px-6 py-4">Category</th>
                        <th className="px-6 py-4">Technology</th>
                        <th className="px-6 py-4">Purpose</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/10">
                      <tr className="hover:bg-surface-container-low transition-colors">
                        <td className="px-6 py-6 font-bold text-primary">Framework</td>
                        <td className="px-6 py-6 text-on-surface-variant">Next.js 15</td>
                        <td className="px-6 py-6 text-on-surface-variant">Full-stack React with App Router and Server Components</td>
                      </tr>
                      <tr className="hover:bg-surface-container-low transition-colors">
                        <td className="px-6 py-6 font-bold text-primary">UI Library</td>
                        <td className="px-6 py-6 text-on-surface-variant">React 19</td>
                        <td className="px-6 py-6 text-on-surface-variant">Component architecture with hooks and concurrent rendering</td>
                      </tr>
                      <tr className="hover:bg-surface-container-low transition-colors">
                        <td className="px-6 py-6 font-bold text-primary">Language</td>
                        <td className="px-6 py-6 text-on-surface-variant">TypeScript</td>
                        <td className="px-6 py-6 text-on-surface-variant">End-to-end type safety across frontend and backend</td>
                      </tr>
                      <tr className="hover:bg-surface-container-low transition-colors">
                        <td className="px-6 py-6 font-bold text-primary">ORM</td>
                        <td className="px-6 py-6 text-on-surface-variant">Prisma</td>
                        <td className="px-6 py-6 text-on-surface-variant">Type-safe database queries and schema migrations</td>
                      </tr>
                      <tr className="hover:bg-surface-container-low transition-colors">
                        <td className="px-6 py-6 font-bold text-primary">Database</td>
                        <td className="px-6 py-6 text-on-surface-variant">Cloudflare D1</td>
                        <td className="px-6 py-6 text-on-surface-variant">Serverless SQLite at the edge</td>
                      </tr>
                      <tr className="hover:bg-surface-container-low transition-colors">
                        <td className="px-6 py-6 font-bold text-primary">Hosting</td>
                        <td className="px-6 py-6 text-on-surface-variant">Cloudflare Pages</td>
                        <td className="px-6 py-6 text-on-surface-variant">Static + SSR deployment with CI/CD</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Deployment Details */}
        <section className="py-24 px-8 bg-surface">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-white font-headline text-center">Deployment Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10">
                <div className="flex items-center gap-3 mb-6">
                  <Cloud className="text-primary" size={24} />
                  <h3 className="text-xl font-bold text-white font-headline">Infrastructure</h3>
                </div>
                <div className="space-y-4 text-on-surface-variant">
                  <p className="text-sm leading-relaxed">1. Git push triggers Cloudflare Pages build pipeline.</p>
                  <p className="text-sm leading-relaxed">2. Wrangler applies D1 schema migrations automatically.</p>
                  <p className="text-sm leading-relaxed">3. Static assets deployed to edge network globally.</p>
                  <p className="text-sm leading-relaxed">4. Server Functions deployed to Cloudflare Workers runtime.</p>
                </div>
              </div>
              <div className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10">
                <div className="flex items-center gap-3 mb-6">
                  <Shield className="text-primary" size={24} />
                  <h3 className="text-xl font-bold text-white font-headline">Security</h3>
                </div>
                <div className="space-y-4 text-on-surface-variant">
                  <p className="text-sm leading-relaxed">1. HTTPS enforced via Cloudflare SSL certificates.</p>
                  <p className="text-sm leading-relaxed">2. Row-level security through user-scoped database queries.</p>
                  <p className="text-sm leading-relaxed">3. CSRF protection on all form submissions.</p>
                  <p className="text-sm leading-relaxed">4. Environment variables managed via Wrangler secrets.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 px-8 bg-surface-container-lowest">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6 text-white font-headline">Interested in the Code?</h2>
            <p className="text-on-surface-variant mb-10 leading-relaxed">
              The full Next.js application with Prisma schema, Server Actions, and Cloudflare deployment configuration is available for review.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="https://github.com/Viresh26"
                target="_blank"
                rel="noopener noreferrer"
                className="px-10 py-4 bg-primary text-on-primary font-headline font-bold rounded-xl transition-all hover:shadow-[0_0_30px_rgba(164,230,255,0.3)]"
              >
                GITHUB REPOSITORY
              </a>
              <Link
                to="/#projects"
                className="px-10 py-4 bg-surface-container-high text-on-surface font-headline font-bold rounded-xl border border-outline-variant/30 hover:bg-surface-container-highest transition-all"
              >
                VIEW ALL PROJECTS
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
    </div>
  )
}
